import { NavLink, useNavigate, useParams } from 'react-router-dom'
import DataTable from '../../../common/data-table/DataTable'
import PageHeader from '../../../layout/page/PageHeader'
import { FilterMatchMode } from 'primereact/api'
import { Button, ButtonGroup } from 'react-bootstrap'
import { useCallback, useEffect, useState } from 'react'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import TrueFalseCell from '../../../common/data-table/TrueFalseCell'
import ViewModule from '../available-module/ViewModule'
import { useAppSelector } from '../../../../store/hooks'
import { ModuleType } from './ModuleRecordForm.d'
import { useToast } from '../../../common/toast/hooks/useToast'
import dayjs from 'dayjs'
import { Calendar } from 'primereact/calendar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons'

type TableRow = Record<string, unknown> & { id?: number; title?: string; updated_at?: string | null; active?: number }
type DateFilterOptions = { value: unknown; filterCallback: (v: unknown, i: number) => void; index: number }

const ModuleRecords = () => {
    const { module: moduleCode } = useParams()
    const [mainPageId, setMainPageId] = useState<number | null>(null)
    const [recordsTemplatePageId, setRecordsTemplatePageId] = useState<
        number | null
    >(null)
    const [refreshTable, setRefreshTable] = useState(false)
    const initData = useAppSelector((state: any) => state.security.initData)
    const [module, setModule] = useState<ModuleType>({
        code: '',
        name: '',
        active: false,
    })

    const forceUpdate = useCallback(
        () => setRefreshTable(!refreshTable),
        [refreshTable]
    )

    const defaultLangId = initData.languages.default.id

    const navigate = useNavigate()
    const { triggerToast } = useToast()

    useEffect(() => {
        if (initData.available_modules.length > 0) {
            const mod = initData.available_modules.filter(
                (m: { code?: string }) => m.code === moduleCode
            )[0]

            if (typeof mod === 'undefined') {
                triggerToast({
                    severity: 'error',
                    summary: 'Module not found!',
                    detail: "Looks like the module you're looking for has been deleted!",
                })

                navigate('/')
            } else {
                setModule(mod)
            }
        } else {
            triggerToast({
                severity: 'error',
                summary: 'Module not found!',
                detail: "Looks like the module you're looking for has been deleted!",
            })

            navigate('/')
        }
    }, [module, initData, moduleCode, forceUpdate, navigate, triggerToast])

    const dateTimeBodyTemplate = (rowData: TableRow) => {
        return rowData.updated_at != null && rowData.updated_at !== undefined
            ? dayjs(rowData.updated_at as string).format('DD-MMM-YYYY @ HH:mm:ss')
            : ''
    }

    const dateFilterTemplate = (options: DateFilterOptions) => {
        return (
            <Calendar
                value={options.value as Date | null | undefined}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
                mask="99/99/9999"
            />
        )
    }

    const statusTemplate = (rowData: TableRow) => {
        return (
            <TrueFalseCell
                value={rowData.active === 1}
                trueLabel="Active"
                falseLabel="Inactive"
            />
        )
    }

    const actionsBodyTemplate = (rowData: TableRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={
                        '/client-modules/' +
                        moduleCode +
                        '/delete/' +
                        rowData.id
                    }
                    record={rowData}
                    deletedCallback={forceUpdate}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete :
                                <strong> {rowData.title}</strong>?
                            </p>
                        </>
                    }
                />
                <NavLink
                    to={'/client/modules/' + moduleCode + '/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    return (
        <>
            <PageHeader
                header={
                    <>
                        <FontAwesomeIcon
                            title={module.active ? 'Active' : 'In-Active'}
                            size="xs"
                            icon={
                                (module.active
                                    ? 'check-circle'
                                    : 'ban') as IconName
                            }
                            className={
                                (module.active
                                    ? 'text-success'
                                    : 'text-danger') + ' me-2'
                            }
                        />
                        {module.name}{' '}
                    </>
                }
                buttons={
                    <>
                        {mainPageId && (
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={() =>
                                    navigate(
                                        '/content/pages/view/' + mainPageId
                                    )
                                }
                            >
                                <FontAwesomeIcon icon={faEdit} />
                                <span className="d-none d-sm-inline-block ms-2">
                                    Main page
                                </span>
                            </Button>
                        )}
                        {recordsTemplatePageId && (
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={() =>
                                    navigate(
                                        '/content/pages/view/' +
                                            recordsTemplatePageId
                                    )
                                }
                            >
                                <FontAwesomeIcon icon={faEdit} />
                                <span className="d-none d-sm-inline-block ms-2">
                                    Records template page
                                </span>
                            </Button>
                        )}
                        <ViewModule
                            code={moduleCode ?? ''}
                            postFormSubmit={forceUpdate}
                        />
                        <Button
                            variant="success"
                            size="sm"
                            onClick={() =>
                                navigate(
                                    '/client/modules/' + moduleCode + '/new'
                                )
                            }
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span className="d-none d-sm-inline-block ms-2">
                                Create New Record
                            </span>
                        </Button>
                    </>
                }
            />

            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                config={{
                    dataRoute:
                        '/client-modules/' +
                        moduleCode +
                        '?langId=' +
                        defaultLangId,
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'title',
                    sortOrder: 1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        title: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        code: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        updated_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                toolbarConfig={{
                    includeSearchBox: true,
                    includeExportBtn: true,
                }}
                columns={[
                    {
                        field: 'code',
                        header: 'Code',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'title',
                        header: 'Title',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        style: {
                            maxWidth: '6rem',
                        },
                    },
                    {
                        field: 'updated_at',
                        header: 'Last Updated',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: dateTimeBodyTemplate,
                        filterElement: dateFilterTemplate,
                        isVisible: false,
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'active',
                        header: 'Active',
                        sortable: true,
                        body: statusTemplate,
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={['code', 'title', 'updated_at']}
                onTableData={(res) => {
                    setMainPageId(res.mainPageId)
                    setRecordsTemplatePageId(res.recordsTemplatePageId)
                }}
            />
        </>
    )
}

export default ModuleRecords
